import React, { type FunctionComponent, Fragment, useState, Component } from 'react'
import moment from 'moment'
import { css } from '@emotion/react'
import { FormattedMessage } from 'react-intl'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { fontSize } from 'gin-and-tonic/foundation/fontSize'
import { Table } from 'legacy/dash-components/table/Table'
import { type DashTableColumns } from 'legacy/dash-blocks/dash-table/utils/dashTable.types'
import { Button } from 'gin-and-tonic/common/button/Button'
import {
  type SandboxOrder,
  type SandboxOrderItem,
  type SandboxOrderSimulationEvent,
} from 'domains/connect/components/fulfillment-simulator/utils/types'
import { SimulateOrderEvent } from 'domains/connect/components/fulfillment-simulator/SimulateOrderEvent'
import { OrderSimulationCallbackLogs } from 'domains/connect/components/fulfillment-simulator/OrderSimulationCallbackLogs'

interface Props {
  sandboxOrder?: SandboxOrder
}

const fieldRowStyle = css`
  display: grid;
  grid-template-columns: 220px 400px;
`
const fieldsetStyle = css`
  margin-bottom: ${spacing.X28};
`
const labelStyle = css`
  text-align: right;
  font-weight: 600;
  padding-right: ${spacing.X12};
  padding-top: ${spacing.X20};
`
const valueStyle = css`
  padding-right: ${spacing.X12};
  padding-top: ${spacing.X20};
`
const legendStyle = css`
  border-bottom: 1px solid grey;
  font-size: ${fontSize.X15};
  font-weight: 600;
  padding-bottom: ${spacing.X12};
  margin-bottom: ${spacing.X20};
  width: 100%;
`

type OrderPropertyRow = {
  id: string
  Cell?: FunctionComponent<any>
}

const DATETIME_FORMAT = 'MMMM D YYYY, h:mm:ss a z'

interface TimeCellProps {
  value?: string
}

const TimeCell: FunctionComponent<TimeCellProps> = ({ value }) => (
  <Fragment>{value && moment.utc(value).format(DATETIME_FORMAT)}</Fragment>
)

export const OrderSimulationOverview: FunctionComponent<Props> = ({ sandboxOrder }) => {
  const [activeSimulationEvent, setActiveSimulationEvent] =
    useState<SandboxOrderSimulationEvent>(null)

  const orderPropertyRows: OrderPropertyRow[] = [
    {
      id: 'id',
    },
    {
      id: 'status',
    },
    {
      id: 'createdAt',
      Cell: ({ value }) => <TimeCell value={value} />,
    },
    {
      id: 'windowStartsAt',
      Cell: ({ value }) => <TimeCell value={value} />,
    },
    {
      id: 'windowEndsAt',
      Cell: ({ value }) => <TimeCell value={value} />,
    },
    {
      id: 'deliveredAt',
      Cell: ({ value }) => <TimeCell value={value} />,
    },
  ]

  const itemColumns: DashTableColumns<SandboxOrderItem> = [
    {
      id: 'lineNum',
      accessor: 'lineNum',
      Header: <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items.lineNum" />,
    },
    {
      id: 'itemUpc',
      accessor: 'itemUpc',
      Header: <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items.itemUpc" />,
    },
    {
      id: 'itemRrc',
      accessor: 'itemRrc',
      Header: <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items.itemRrc" />,
    },
    {
      id: 'qty',
      accessor: 'qty',
      Header: <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items.qty" />,
    },
    {
      id: 'qtyUnit',
      accessor: 'qtyUnit',
      Header: <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items.qtyUnit" />,
    },
    {
      id: 'replaced',
      Header: <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items.replaced" />,
      Cell: ({
        row: {
          original: { replaced },
        },
      }) => <div>{replaced.toString()}</div>,
    },
  ]
  const eventColumns: DashTableColumns<SandboxOrderSimulationEvent> = [
    {
      id: 'event',
      accessor: 'event',
      Header: (
        <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents.event" />
      ),
    },
    {
      id: 'arguments',
      Header: (
        <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents.arguments" />
      ),
      Cell: ({ row: { original } }) => (
        <div>{original.arguments.length == 0 ? 'N/A' : original.arguments.join(',')}</div>
      ),
    },
    {
      id: 'outcome.orderStatus',
      Header: (
        <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents.outcome.orderStatus" />
      ),
      Cell: ({
        row: {
          original: {
            outcome: { orderStatus },
          },
        },
      }) => <div>{orderStatus}</div>,
    },
    {
      id: 'outcome.callback',
      Header: (
        <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents.outcome.callback" />
      ),
      Cell: ({
        row: {
          original: {
            outcome: { callback },
          },
        },
      }) => <div>{callback || 'N/A'}</div>,
    },
    {
      id: 'simulate',
      Header: (
        <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents.actions" />
      ),
      Cell: ({ row: { original } }) => (
        <Fragment>
          <Button onClick={() => setActiveSimulationEvent(original)}>
            <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents.actions.simulate" />
          </Button>
        </Fragment>
      ),
    },
  ]

  const simulateEventModal = (
    <SimulateOrderEvent
      sandboxOrder={sandboxOrder}
      simulationEvent={activeSimulationEvent}
      handleClose={() => setActiveSimulationEvent(null)}
    />
  )

  return (
    <Fragment>
      <div style={{ width: '50vw' }}>
        <div css={fieldsetStyle}>
          <legend css={legendStyle}>
            <FormattedMessage id="connect.fulfillmentSimulator.orderProps" />
          </legend>
          {orderPropertyRows.map(rowSpec => (
            <div css={fieldRowStyle} key={rowSpec.id}>
              <span css={labelStyle}>
                <FormattedMessage id={`connect.fulfillmentSimulator.orderProps.${rowSpec.id}`} />
              </span>
              <span css={valueStyle}>
                {sandboxOrder &&
                  (rowSpec.Cell ? (
                    <rowSpec.Cell value={sandboxOrder[rowSpec.id]} />
                  ) : (
                    sandboxOrder[rowSpec.id]
                  ))}
              </span>
            </div>
          ))}
        </div>
        <div css={fieldsetStyle}>
          <legend css={legendStyle}>
            <FormattedMessage id="connect.fulfillmentSimulator.orderProps.items" />
          </legend>
          <Table
            initialPageSize={10}
            hidePageSizeSelector
            tableWrapperOverrideStyle={{ paddingLeft: spacing.X20, paddingRight: spacing.X20 }}
            columns={itemColumns}
            data={(sandboxOrder && sandboxOrder.items) || []}
          />
        </div>
        <div css={fieldsetStyle}>
          <legend css={legendStyle}>
            <FormattedMessage id="connect.fulfillmentSimulator.orderProps.simulationEvents" />
          </legend>
          <Table
            initialPageSize={10}
            hidePageSizeSelector
            tableWrapperOverrideStyle={{ paddingLeft: spacing.X20, paddingRight: spacing.X20 }}
            columns={eventColumns}
            data={(sandboxOrder && sandboxOrder.simulationEvents) || []}
          />
        </div>
        <div css={fieldsetStyle}>
          <legend css={legendStyle}>
            <FormattedMessage id="connect.fulfillmentSimulator.orderProps.callbackLogs" />
          </legend>
          <OrderSimulationCallbackLogs sandboxOrder={sandboxOrder} />
        </div>
        {activeSimulationEvent && simulateEventModal}
      </div>
    </Fragment>
  )
}
